/**
 * Conversation Context — message history for multi-turn direction.
 *
 * Providers receive the context alongside each prompt so future flows can
 * iterate ("make it warmer", "less text") without rebuilding state. A
 * simple character budget keeps the payload bounded; trimming drops the
 * oldest non-system messages first, which is the standard LLM pattern.
 */

const DEFAULT_BUDGET = 24000; // characters, ≈6k tokens — generous for briefs

export class ConversationContext {
  constructor({ budget = DEFAULT_BUDGET } = {}) {
    this.budget = budget;
    this.messages = []; // { role: 'system'|'user'|'assistant'|'function', content, name? }
  }

  add(role, content, name) {
    this.messages.push({ role, content, ...(name ? { name } : {}) });
    this._trim();
    return this;
  }

  addUser(content) { return this.add('user', content); }
  addAssistant(content) { return this.add('assistant', content); }
  addFunctionResult(name, result) {
    return this.add('function', JSON.stringify(result), name);
  }

  /** Payload shape providers consume — a plain message array. */
  toPayload() {
    return this.messages.map((m) => ({ ...m }));
  }

  get size() {
    return this.messages.length;
  }

  clear() {
    this.messages = [];
    return this;
  }

  _trim() {
    const total = () => this.messages.reduce((s, m) => s + m.content.length, 0);
    while (total() > this.budget && this.messages.length > 1) {
      const idx = this.messages.findIndex((m) => m.role !== 'system');
      if (idx === -1) break; // only system messages left — never drop those
      this.messages.splice(idx, 1);
    }
  }
}
